import React, { useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { alpha, Box } from '@mui/material';
import { CalendarOptions } from '@fullcalendar/core';
import { HEADER_HEIGHT } from 'theme/constants';
import { Loader } from 'components';
import { useApiRequest, useFiltersSearchParams } from 'hooks';
import { QueryObserverResult } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import DayCellContent from './DayCellContent';
import EventContent from './EventContent';
import DataTable from '../DataTableProps';
import { marketing, MarketingTypes } from '../../../api/marketing';

interface Props {
  data: MarketingTypes.ListData['data'];
  isLoading: boolean;
  setDateRange: (value: DataTable.DateFilterOption['value']) => void;
  refetch: () => Promise<QueryObserverResult>;
}

const FullCalendarPage = (props: Props) => {
  const { isLoading, data, setDateRange, refetch } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { updateFiltersSearchParams } = useFiltersSearchParams();

  const { requestFn, isLoading: isChangingDate } = useApiRequest((args) =>
    marketing.update(args.marketing_id, args.data),
  );

  const updateDealStartDate = useCallback(
    async (payload: any) => {
      const dealId = payload.event._def.extendedProps.marketing_id;
      const dealStatus = payload.event._def.extendedProps.status;
      const newDate = payload.event.startStr;

      if (dealStatus === 'Posted' || dealStatus === 'Finished') {
        payload.revert();
        enqueueSnackbar({
          variant: 'warning',
          message: `Marketing can not be moved`,
        });
      } else {
        await requestFn({
          args: {
            marketing_id: dealId,
            data: { start_date: newDate },
          },
          successMessage: 'Deal date changed',
        });

        await refetch();
      }
    },
    [refetch, requestFn, enqueueSnackbar],
  );

  const config: CalendarOptions = useMemo(
    () => ({
      buttonText: {
        month: 'Month',
        today: 'Today',
        week: 'Week',
      },
      datesSet: (args) => {
        setDateRange({
          $gte: args.startStr,
          $lte: args.endStr,
        });
      },
      eventChange: updateDealStartDate,

      // eslint-disable-next-line react/no-unstable-nested-components
      dayCellContent: (d) => <DayCellContent arg={d} />,
      // eslint-disable-next-line react/no-unstable-nested-components
      eventContent: (e) => <EventContent eventInfo={e} />,
      eventClick: (eventInfo) =>
        updateFiltersSearchParams({
          marketingPlanId: eventInfo.event.extendedProps.marketing_id,
        }),
      dayHeaderFormat: { weekday: 'long' },
      editable: true,
      events: (data || []).map((d) => ({
        date: d.start_date,
        end: d.end_date || '',
        title: d.coupon_code,
        brand: d.brand,
        country_team: d.country_team,
        marketing_id: d._id.$oid,
      })),
      firstDay: 1,
      fixedWeekCount: false,
      headerToolbar: {
        left: 'prev next today',
        center: 'title',
        right: 'dayGridWeek dayGridMonth',
      },
      height: `calc(100vh - 34px - ${HEADER_HEIGHT}px - 160px)`,
      initialView: 'dayGridWeek',
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      selectable: true,
      selectMirror: true,
      views: {
        dayGridMonth: {
          dayMaxEvents: 3,
        },
        dayGridWeek: {
          dayMaxEvents: 10,
        },
      },
      weekends: true,
    }),
    [data, setDateRange, updateDealStartDate, updateFiltersSearchParams],
  );

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        '& .fc-toolbar-title': {
          fontWeight: 400,
        },
        '& .fc .fc-daygrid-day.fc-day-today': {
          backgroundColor: alpha(theme.palette.secondary.main, 0.15),
        },
        '& .fc-button.fc-button-primary': {
          borderRadius: `${theme.shape.borderRadius}px`,
          backgroundColor: theme.palette.primary.main,
          border: 0,
          '&:disabled': {
            backgroundColor: theme.palette.primary.light,
          },
          '&:hover, &:not(:disabled).fc-button-active': {
            backgroundColor: theme.palette.primary.dark,
          },
        },
        '& .fc-h-event': {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
        },
        '& .fc-theme-standard .fc-popover-header': {
          backgroundColor: 'unset',
        },
        '& .fc-popover': {
          borderRadius: `${theme.shape.borderRadius}px`,
          padding: 1,
          overflow: 'hidden',
          // transform: 'translate(-250px, -200px)',
          '&-header': {
            fontSize: '16px',
            fontWeight: 500,
          },
          '&-body': {
            maxHeight: 350,
            overflowY: 'auto',
          },
        },
      })}
    >
      {(isLoading || isChangingDate) && <Loader variant="fullScreen" />}
      <FullCalendar {...config} />
    </Box>
  );
};

export default FullCalendarPage;
